<template>
  <div class="smeetz-cropper">
    <img ref="imgRef" :src="src" alt="cropper" class="smeetz-cropper__img" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

const props = withDefaults(
  defineProps<{
    src: string;
    aspectRatio?: number;
    viewMode?: 0 | 1 | 2 | 3;
    autoCropArea?: number;
  }>(),
  {
    aspectRatio: NaN,
    viewMode: 1,
    autoCropArea: 1,
  }
);

const imgRef = ref<HTMLImageElement | null>(null);
let cropperInstance: Cropper | null = null;

function destroyCropper() {
  if (cropperInstance) {
    cropperInstance.destroy();
    cropperInstance = null;
  }
}

function createCropper() {
  if (!imgRef.value) return;
  destroyCropper();
  cropperInstance = new Cropper(imgRef.value, {
    aspectRatio: props.aspectRatio,
    viewMode: props.viewMode,
    autoCropArea: props.autoCropArea,
    responsive: true,
    background: true,
    movable: true,
    zoomable: true,
    rotatable: false,
    scalable: false,
  });
}

onMounted(async () => {
  await nextTick();
  createCropper();
});

watch(
  () => props.src,
  async () => {
    await nextTick();
    createCropper();
  }
);

onBeforeUnmount(destroyCropper);

defineExpose({
  /**
   * Return the cropped canvas. The caller can call `.toBlob(...)` or `.toDataURL(...)` on it.
   * @param {Cropper.GetCroppedCanvasOptions} [options]
   * @returns {HTMLCanvasElement | null}
   */
  getCroppedCanvas: (options?: Cropper.GetCroppedCanvasOptions) =>
    cropperInstance ? cropperInstance.getCroppedCanvas(options) : null,
  /**
   * Destroy the cropper instance and detach listeners.
   * @returns {void}
   */
  destroy: destroyCropper,
});
</script>

<style scoped>
.smeetz-cropper {
  width: 100%;
  height: 100%;
}

.smeetz-cropper__img {
  display: block;
  max-width: 100%;
}
</style>
